/**
 * SSO-Enhanced Login Form
 * Login form with support for traditional email/password and SSO providers
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Lock,
  LogIn,
  Loader2,
  Shield,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../lib/auth/AuthContext';
import { ssoService, SSOConfig, SSOProvider } from '../../lib/auth/sso-service';

interface SSOButtonProps {
  provider: SSOConfig;
  onSSOLogin: (provider: SSOProvider) => Promise<void>;
  disabled: boolean;
}

const PROVIDER_NAMES = {
  google: 'Google',
  microsoft: 'Microsoft',
  okta: 'Okta',
  auth0: 'Auth0',
  saml: 'SAML'
} as const;

const PROVIDER_ICONS = {
  google: '🔍',
  microsoft: '🏢',
  okta: '🔑',
  auth0: '🛡️',
  saml: '📋'
} as const;

const PROVIDER_STYLES = {
  google: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
  microsoft: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
  okta: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
  auth0: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100',
  saml: 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
} as const;

const SSOButton: React.FC<SSOButtonProps> = ({ provider, onSSOLogin, disabled }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await onSSOLogin(provider.provider);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={disabled || loading}
      className={`w-full justify-start ${PROVIDER_STYLES[provider.provider]}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-lg">{PROVIDER_ICONS[provider.provider]}</span>
        <span>
          {loading ? (
            <>
              <Loader2 className="inline h-4 w-4 mr-2 animate-spin" />
              Signing in...
            </>
          ) : (
            `Continue with ${PROVIDER_NAMES[provider.provider]}`
          )}
        </span>
      </div>
    </Button>
  );
};

export const SSOLoginForm: React.FC = () => {
  const { toast } = useToast();
  const { login, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfaCode: ''
  });
  const [showMFA, setShowMFA] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [ssoProviders, setSSOProviders] = useState<SSOConfig[]>([]);
  const [ssoLoading, setSSOLoading] = useState(false);

  const redirectTo = searchParams.get('redirect') || '/overview';
  const errorMessage = searchParams.get('error');
  const ssoError = searchParams.get('sso_error');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  // Load SSO providers
  useEffect(() => {
    loadSSOProviders();
  }, []);

  // Show error messages from URL params
  useEffect(() => {
    if (errorMessage) {
      toast({
        title: 'Authentication Failed',
        description: decodeURIComponent(errorMessage),
        variant: 'destructive'
      });
    }

    if (ssoError) {
      toast({
        title: 'SSO Login Failed',
        description: decodeURIComponent(ssoError),
        variant: 'destructive'
      });
    }
  }, [errorMessage, ssoError, toast]);

  const loadSSOProviders = async () => {
    try {
      const providers = await ssoService.getAvailableProviders();
      setSSOProviders(providers.filter(p => p.enabled));
    } catch (error) {
      console.error('Failed to load SSO providers:', error);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTraditionalLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast({
        title: 'Missing Fields',
        description: 'Please enter your email and password',
        variant: 'destructive'
      });
      return;
    }

    setLoginLoading(true);

    try {
      await login(formData.email, formData.password, formData.mfaCode || undefined);
      navigate(redirectTo);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';

      if (errorMessage.toLowerCase().includes('mfa') || errorMessage.toLowerCase().includes('2fa')) {
        setShowMFA(true);
        toast({
          title: 'MFA Required',
          description: 'Please enter your multi-factor authentication code',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Login Failed',
          description: errorMessage,
          variant: 'destructive'
        });
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSSOLogin = async (provider: SSOProvider) => {
    setSSOLoading(true);

    try {
      const authUrl = await ssoService.initiateOAuth({
        provider,
        redirect_url: redirectTo,
        state: crypto.randomUUID()
      });

      // Redirect to SSO provider
      window.location.href = authUrl;
    } catch (error) {
      setSSOLoading(false);
      toast({
        title: 'SSO Error',
        description: error instanceof Error ? error.message : 'Failed to initiate SSO login',
        variant: 'destructive'
      });
    }
  };

  const hasEnabledProviders = ssoProviders.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-10 w-10 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
          <p className="text-gray-600">Sign in to your Briefcase AI account</p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* SSO Providers */}
          {hasEnabledProviders && (
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">Choose your preferred sign-in method</p>
              </div>

              {ssoProviders.map((provider) => (
                <SSOButton
                  key={provider.provider}
                  provider={provider}
                  onSSOLogin={handleSSOLogin}
                  disabled={ssoLoading || loginLoading}
                />
              ))}

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>
            </div>
          )}

          {/* Traditional Login Form */}
          <form onSubmit={handleTraditionalLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10"
                  disabled={loginLoading || ssoLoading}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Your password"
                  className="pl-10"
                  disabled={loginLoading || ssoLoading}
                  required
                />
              </div>
            </div>

            {showMFA && (
              <div className="space-y-2">
                <Label htmlFor="mfaCode">Authentication Code</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  value={formData.mfaCode}
                  onChange={(e) => handleInputChange('mfaCode', e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  disabled={loginLoading || ssoLoading}
                />
                <p className="text-xs text-gray-600">
                  Enter the code from your authenticator app
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loginLoading || ssoLoading || isLoading}
            >
              {loginLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* Help Links */}
          <div className="text-center space-y-2">
            <Link
              to="/auth/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Forgot your password?
            </Link>
          </div>

          {/* SSO Help */}
          {!hasEnabledProviders && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No SSO providers are configured. Contact your administrator to set up single sign-on.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SSOLoginForm;